import { createPortal } from 'react-dom';
import { useState } from 'react';
import { X, ExternalLink, Download, ChevronLeft, ChevronRight, FileText, Image as ImageIcon, Ruler, BarChart3 } from 'lucide-react';

// Simple fullscreen modal for viewing a list of attachments (Gantt charts or
// AutoCAD plans). Handles images (inline preview) and PDFs/DWG/DXF (open in
// a new tab). Rendered via a React Portal so it escapes any parent overflow.

function fileExt(url) {
  if (!url) return '';
  const clean = url.split('?')[0].split('#')[0];
  const dot = clean.lastIndexOf('.');
  return dot === -1 ? '' : clean.slice(dot + 1).toLowerCase();
}

function isImageExt(ext) { return ['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg'].includes(ext); }

// For DXF uploads we have a sibling .jpg preview generated server-side.
function dxfThumb(url) {
  if (!url) return null;
  return /\.dxf(\?.*)?$/i.test(url) ? url.replace(/\.dxf(\?.*)?$/i, '.jpg') : null;
}

export default function AttachmentsViewer({ open, onClose, items, title, accentColor = '#FF6F59', kind = 'gantt', milestones = [] }) {
  const [idx, setIdx] = useState(0);
  if (!open || typeof document === 'undefined') return null;
  const list = items || [];
  const current = list[idx] || null;
  const ext = fileExt(current);
  const preview = isImageExt(ext) ? current : dxfThumb(current);
  const Icon = kind === 'cad' ? Ruler : BarChart3;
  const filename = current ? current.split('/').pop() : '';

  const prev = () => setIdx((p) => (list.length ? (p - 1 + list.length) % list.length : 0));
  const next = () => setIdx((p) => (list.length ? (p + 1) % list.length : 0));

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/85 p-4"
      style={{ zIndex: 10004 }}
      onClick={onClose}
      data-testid={`attachments-viewer-${kind}`}
    >
      <div
        className="relative w-full max-w-5xl max-h-[92vh] rounded-2xl overflow-hidden flex flex-col"
        style={{ backgroundColor: '#F4F1EB' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3" style={{ backgroundColor: accentColor, color: 'white' }}>
          <div className="flex items-center gap-2 min-w-0">
            <Icon className="w-5 h-5 flex-shrink-0" />
            <h3 className="text-base sm:text-lg font-bold truncate" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {title}
            </h3>
            {list.length > 1 && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/20 flex-shrink-0">
                {idx + 1} / {list.length}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            type="button"
            className="w-11 h-11 rounded-full bg-white text-slate-900 hover:bg-red-50 hover:scale-110 active:scale-90 transition-transform duration-75 flex items-center justify-center flex-shrink-0 shadow-md cursor-pointer"
            style={{ touchAction: 'manipulation' }}
            aria-label="Close"
            data-testid={`attachments-viewer-close-${kind}`}
          >
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden relative" style={{ backgroundColor: '#1A2E2E' }}>
          {list.length === 0 ? (
            <div className="h-full min-h-[30vh] flex flex-col items-center justify-center gap-2 p-6" style={{ color: '#A3D4D4', fontFamily: 'Manrope' }}>
              <Icon className="w-10 h-10 opacity-40" />
              <p className="text-sm text-center max-w-md">
                {kind === 'gantt' && milestones.length > 0
                  ? 'No Gantt chart image/PDF uploaded yet. The milestone list below reflects the planned schedule.'
                  : `No ${kind === 'cad' ? 'AutoCAD plans' : 'Gantt charts'} uploaded for this area yet.`}
              </p>
            </div>
          ) : preview ? (
            <img
              src={preview}
              alt={filename}
              className="w-full h-full object-contain bg-white"
              style={{ maxHeight: '70vh' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="h-full min-h-[50vh] flex flex-col items-center justify-center gap-3 px-6" style={{ color: '#A3D4D4', fontFamily: 'Manrope' }}>
              {ext === 'pdf' ? <FileText className="w-12 h-12" /> : <ImageIcon className="w-12 h-12" />}
              <p className="text-sm font-bold uppercase tracking-wide">{ext ? `.${ext}` : 'Document'}</p>
              <p className="text-xs text-center max-w-md opacity-80">
                This file cannot be previewed inline. Click "Open file" below to view it in a new tab.
              </p>
            </div>
          )}

          {list.length > 1 && (
            <>
              <button
                onClick={prev}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-gray-800 flex items-center justify-center shadow-lg"
                aria-label="Previous"
                data-testid={`attachments-viewer-prev-${kind}`}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={next}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-gray-800 flex items-center justify-center shadow-lg"
                aria-label="Next"
                data-testid={`attachments-viewer-next-${kind}`}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
        </div>

        {/* Milestones timeline — only shown for gantt kind */}
        {kind === 'gantt' && milestones.length > 0 && (
          <div className="px-4 sm:px-5 py-3 border-t" style={{ backgroundColor: 'white', borderColor: '#EAE5D9' }}>
            <p className="text-[10px] font-bold tracking-[0.15em] uppercase mb-2" style={{ color: accentColor, fontFamily: 'Manrope' }}>
              Milestones ({milestones.length})
            </p>
            <ul className="space-y-2 max-h-48 overflow-y-auto" data-testid="milestones-timeline">
              {milestones.map((m, i) => {
                const pct = Math.max(0, Math.min(100, Number(m.progress_percent) || 0));
                const due = m.end_date || m.start_date || '—';
                const priorityColor = m.priority === 'urgent' ? '#EF4444' : m.priority === 'low' ? '#7A8C8C' : accentColor;
                return (
                  <li key={m.id || i} className="rounded-lg p-2" style={{ backgroundColor: '#F4F1EB' }}>
                    <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-bold truncate" style={{ fontFamily: 'Manrope', color: '#1A2E2E' }}>{m.title}</span>
                      <span className="flex items-center gap-1.5 text-[10px] font-bold flex-shrink-0" style={{ fontFamily: 'Manrope', color: '#4A5D5D' }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: priorityColor }} />
                        <span>{due}</span>
                        <span className="tabular-nums">· {pct}%</span>
                      </span>
                    </div>
                    <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: '#EAE5D9' }}>
                      <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: priorityColor }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Footer */}
        {current && (
          <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 flex-wrap" style={{ backgroundColor: '#EAE5D9' }}>
            <span className="text-[11px] font-mono truncate min-w-0 flex-1" style={{ color: '#4A5D5D' }}>{filename}</span>
            <div className="flex gap-2 flex-shrink-0">
              <a
                href={current}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-white"
                style={{ backgroundColor: accentColor, fontFamily: 'Manrope' }}
                data-testid={`attachments-viewer-open-${kind}`}
              >
                <ExternalLink className="w-3 h-3" /> Open file
              </a>
              <a
                href={current}
                download
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold"
                style={{ backgroundColor: 'white', color: accentColor, fontFamily: 'Manrope', border: `1px solid ${accentColor}` }}
              >
                <Download className="w-3 h-3" /> Download
              </a>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
